import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import BetslipSettings from 'modules/betslip/betslip-view/settings/index.js';
import SingleBetsList from 'modules/betslip/betslip-view/single-bets-list/index.js';
import SystemBetsList from 'modules/betslip/betslip-view/system-bets-list/index.js';
import TeaserOptions from 'modules/betslip/betslip-view/teaser-options/index.js';
import BetslipModuleUtils from 'modules/betslip/utils.js';
import ShareBetslip from 'modules/share-betslip/index.js';
import { isEmpty } from 'mojito/utils';
import RemoveAllButton from 'modules/betslip/betslip-view/bet-builder-components/remove-all-button.js';

const {
    actions: betslipActions,
    utils: BetslipUtils,
    types: BetslipTypes,
} = MojitoServices.Betslip;
const { useSubConfig } = MojitoCore.Presentation.Hooks;
const { SelectableButton, SelectableButtonGroup, Text, FlexPane, ToggleButton, FlexPaneSeparator } =
    MojitoPresentation.Components;

const BetBuilder = props => {
    const { mojitoTools, betslip, stakeGroup, bankersActivated } = props;
    const { config, stringResolver } = mojitoTools;

    const hasBetContent = BetslipModuleUtils.hasContent(betslip);

    const tabProps = { config, stringResolver, stakeGroup, bankersActivated };

    return (
        <FlexPane class={'ta-BetBuilder'} config={config.topContainer}>
            {config.showTabs && <Tabs {...tabProps} />}
            {hasBetContent ? <ActiveTabContent {...props} /> : <EmptyTabContent {...tabProps} />}
        </FlexPane>
    );
};

BetBuilder.getStyle = (config, mode, merge) => ({
    shareSingleBetsList: merge(config.singleBetsList, config.shareSingleBetsList),
});

export default BetBuilder;

const Tabs = ({ config, stringResolver, stakeGroup, bankersActivated }) => {
    const dispatch = useDispatch();
    const { SINGLES, MULTIPLES, BANKERS, SYSTEMS, TEASERS } = BetslipTypes.STAKE_GROUP_NAME;

    const activateStakeGroup = nextStakeGroup => {
        const shouldActivateBankers = nextStakeGroup === SYSTEMS && bankersActivated;
        dispatch(
            betslipActions.activateStakeGroup(shouldActivateBankers ? BANKERS : nextStakeGroup)
        );
    };

    const isSystemsTab = BetslipModuleUtils.isSystemsTab(stakeGroup);
    const selectedKey = isSystemsTab
        ? SYSTEMS
        : BetslipTypes.STAKE_GROUP_NAME[stakeGroup.toUpperCase()];

    return (
        <SelectableButtonGroup
            selectedKey={selectedKey}
            onSelectionChange={activateStakeGroup}
            config={config.tabs}
            class="ta-Betslip-bet_type_navigation"
        >
            <SelectableButton class={'ta-Single-tab'} selectableKey={SINGLES} key={SINGLES}>
                {stringResolver.resolveString('$BETSLIP.STAKE_GROUP_NAME.SINGLES')}
            </SelectableButton>
            <SelectableButton class={'ta-Multi-tab'} selectableKey={MULTIPLES} key={MULTIPLES}>
                {stringResolver.resolveString('$BETSLIP.STAKE_GROUP_NAME.MULTIPLES')}
            </SelectableButton>
            <SelectableButton class={'ta-Systems-tab'} selectableKey={SYSTEMS} key={SYSTEMS}>
                {stringResolver.resolveString('$BETSLIP.STAKE_GROUP_NAME.SYSTEMS')}
            </SelectableButton>
            {config.showTeasers && (
                <SelectableButton class={'ta-Teasers-tab'} selectableKey={TEASERS} key={TEASERS}>
                    {stringResolver.resolveString('$BETSLIP.STAKE_GROUP_NAME.TEASERS')}
                </SelectableButton>
            )}
        </SelectableButtonGroup>
    );
};

const EmptyTabContent = ({ config, stringResolver, stakeGroup }) => {
    const text = stringResolver.resolveString(`$BETSLIP.EMPTY_TAB.${stakeGroup.toUpperCase()}`);
    return (
        <FlexPane class="ta-BetBuilderEmptyTab" config={config.emptyContentContainer}>
            <Text class="ta-BetBuilderEmptyTabText" config={config.emptyContentText}>
                {text}
            </Text>
        </FlexPane>
    );
};

const ActiveTabContent = props => {
    const {
        stakeGroup,
        betslip,
        currencyCode,
        betslipStatus,
        hasPendingBonus,
        isStakeGroupPending,
        mojitoTools: { style, config, stringResolver },
    } = props;
    const {
        showBetslipSettings: hasSettings,
        systemBetsList,
        showBankers,
        showGroupedSingles,
    } = config;
    const { SINGLES, MULTIPLES } = BetslipTypes.STAKE_GROUP_NAME;

    const isSinglesTab = stakeGroup === SINGLES;
    const isMultiplesTab = stakeGroup === MULTIPLES;
    const isTeasersTab = BetslipUtils.isTeaser(stakeGroup);
    const isSystemsTab = BetslipModuleUtils.isSystemsTab(stakeGroup);
    const sanitizedBetslipForShare = useMemo(
        () => BetslipModuleUtils.sanitizeBetslipForShare(betslip),
        [betslip]
    );
    const betRefs = useMemo(() => BetslipUtils.extractBetRefs(betslip), [betslip]);

    // For system tab, hide system bets while switching stake group. It is requirement for Bankers.
    const hasSystemBets = isMultiplesTab || (isSystemsTab && !isStakeGroupPending) || isTeasersTab;

    const enableGroupedSinglesStaking = !isSinglesTab;
    const hasTeaserOptions = !!betslip.teaserOptions;

    const singleBetsProps = {
        betslip,
        stakeGroup,
        isSinglesTab,
        betslipStatus,
        hasPendingBonus,
        config,
    };

    const removeAllConfig = useSubConfig(config, 'removeAllButton', 'removeAllButtonText');
    return (
        <FlexPane class={`ta-ActiveTabContent ${stakeGroup}`} config={config.contentContainer}>
            <RemoveAllButton
                class="ta-BetBuilderActiveTabRemoveAllButton"
                config={removeAllConfig}
                stringResolver={stringResolver}
            />
            <FlexPane
                class={'ta-SinglesContainer'}
                config={config.teaserOptionsAndSinglesContainer}
            >
                {isTeasersTab && hasTeaserOptions && (
                    <TeaserOptions
                        class="ta-BetBuilderTeaserOptions"
                        config={config.teaserOptions}
                        selectedTeaserType={BetslipUtils.getActiveTeaserType(betslip)}
                        options={betslip.teaserOptions}
                    />
                )}
                <SingleBets class="ta-BetBuilderSingleBets" {...singleBetsProps} />
            </FlexPane>
            <Options config={config}>
                {hasSettings && <Settings config={config} />}
                <ShareBetslip
                    class="ta-BetBuilderShareBetslip"
                    betRefs={betRefs}
                    betslipId={sanitizedBetslipForShare.bets.map(bet => bet.id).join('-')}
                >
                    <SingleBetsList
                        config={style.shareSingleBetsList}
                        betslip={sanitizedBetslipForShare}
                        activeStakeGroup={stakeGroup}
                        betslipStatus={betslipStatus}
                        highlightEnabled={false}
                        bankersEnabled={false}
                        stakingEnabled={false}
                    />
                </ShareBetslip>
                {showBankers && isSystemsTab && <BankersSwitch {...props} />}
            </Options>
            {!hasSystemBets && (
                <FlexPaneSeparator direction={'column'} config={config.singleBetsSeparator} />
            )}
            {hasSystemBets && (
                <SystemBetsList
                    includeGroupedSingles={showGroupedSingles}
                    config={systemBetsList}
                    enableGroupedSinglesStaking={enableGroupedSinglesStaking}
                    hasPendingBonus={hasPendingBonus}
                    currencyCode={currencyCode}
                    betslip={betslip}
                    stakeGroup={stakeGroup}
                />
            )}
        </FlexPane>
    );
};

const SingleBets = ({
    betslip,
    stakeGroup,
    betslipStatus,
    hasPendingBonus,
    config,
    class: className,
}) => {
    const isEWSystemInvolved = BetslipUtils.getSystemBets(betslip, true).some(bet =>
        BetslipUtils.isBetEachWay(betslip, bet.id, stakeGroup)
    );
    const { SINGLES } = BetslipTypes.STAKE_GROUP_NAME;

    const isSinglesTab = stakeGroup === SINGLES;
    return (
        <SingleBetsList
            class={className}
            config={config.singleBetsList}
            betslip={betslip}
            activeStakeGroup={stakeGroup}
            betslipStatus={betslipStatus}
            hasPendingBonus={hasPendingBonus}
            highlightEnabled={!isSinglesTab}
            forceEWLabelsToShow={isEWSystemInvolved}
        />
    );
};

const Options = ({ config, children }) => {
    return !isEmpty(children?.filter(Boolean)) ? (
        <FlexPane config={config.optionsContainer} class="ta-Betslip-bet_options">
            {children}
        </FlexPane>
    ) : null;
};

const Settings = ({ config }) => {
    return (
        <FlexPane class="ta-BetBuilderSettings" config={config.betslipSettingsContainer}>
            <BetslipSettings config={config.betslipSettings} />
        </FlexPane>
    );
};

const BankersSwitch = ({
    mojitoTools: { config, stringResolver },
    bankersActivated,
    isStakeGroupPending,
    hasPendingBankerBet,
}) => {
    const dispatch = useDispatch();
    return (
        <ToggleButton
            config={config.bankersToggleButton}
            value={bankersActivated}
            pending={isStakeGroupPending}
            disabled={hasPendingBankerBet && !isStakeGroupPending}
            onToggle={() => dispatch(betslipActions.toggleBankersMode(!bankersActivated))}
            class="ta-BankersSwitch"
            label={stringResolver.resolveString('$BETSLIP.BET_OPTIONS.BANKERS')}
        />
    );
};
